import {
    GridApiCommon,
    GridCellModes,
    GridRowId,
    GridRowModes,
    GridRowModesModel,
    GridRowSelectionModel,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { create } from "zustand";

import { HotelClinicStatus } from "@/models/hotel-clinic.model";
import { HotelClinicTaskResolved } from "@/pages/HotelPlanning/clinic-arrival/types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export enum ClinicArrivalTasksBulkConfirmTypes {
    STATUS = "status",
    DELETE = "delete",
}

export type ClinicArrivalTasksBulkConfirmStatusState = {
    type: ClinicArrivalTasksBulkConfirmTypes.STATUS;
    tasks: {
        taskId: string;
        updateData: { status: HotelClinicStatus };
    }[];
};

export type ClinicArrivalTasksBulkConfirmDeleteState = {
    type: ClinicArrivalTasksBulkConfirmTypes.DELETE;
    tasks: {
        taskId: string;
        updateData: { deleteTask: true };
    }[];
};

export type ClinicArrivalTasksBulkConfirmState =
    | ClinicArrivalTasksBulkConfirmStatusState
    | ClinicArrivalTasksBulkConfirmDeleteState;

export type ShowPassengerStatusUpdateDialogType = {
    passengerRowId: string;
    current: HotelClinicStatus;
    next: HotelClinicStatus;
} | null;

export type ShowTasksDetailUnassignDialogType = {
    taskId: string;
    /** Resolved passenger `listItemId` values for PATCH `/unassign` body `passengers` */
    listItemIds: string[];
    /** Real passengers in the detail grid (excluding DnD placeholders) */
    totalPassengers: number;
} | null;

/** Pending grid edits per row (extend when adding editable columns). */
export type ClinicTasksEditableFields = {
    pickUp?: string;
};

/** Fields that participate in cancel / cell restore (keep in sync with editable columns). */
export const clinicTasksEditableFieldKeysToCancel: (keyof ClinicTasksEditableFields)[] = ["pickUp"];

function taskRowKey(row: HotelClinicTaskResolved): GridRowId {
    return row.id;
}

export function normalizeTaskPickUp(val: unknown): string {
    if (val == null || val === "") return "";
    if (typeof val === "string") {
        const dt = DateTime.fromISO(val, { zone: "utc" }).setZone("utc");
        return dt.isValid ? (dt.toUTC().toISO() ?? val) : val;
    }
    if (val instanceof DateTime) {
        return val.isValid ? (val.toUTC().toISO() ?? "") : "";
    }

    return "";
}

type ClinicArrivalTasksGridState = {
    selectedRows: GridRowSelectionModel;
    expandedRows: GridRowId[];
    showPassengerStatusUpdateDialog: ShowPassengerStatusUpdateDialogType;
    /** `null` = all tasks */
    statusFilter: HotelClinicStatus | null;
    /** Open full-screen-style dialog with task milestone history */
    taskStatusHistoryTaskId: string | null;
    bulkTasksConfirm: ClinicArrivalTasksBulkConfirmState | null;
    isFullScreen: boolean;
    /** Row edit modes for tasks grid cell editing. */
    rowModesModel: GridRowModesModel;
    /** Unsaved edits keyed by row id. */
    changes: Record<GridRowId, ClinicTasksEditableFields>;
    /** Original values when an edit started (for cancel / diff). */
    initialValues: Record<GridRowId, ClinicTasksEditableFields>;
    /** Batch-save confirmation dialog open. */
    saveConfirmOpen: boolean;
    showTasksDetailUnassignDialog: ShowTasksDetailUnassignDialogType;
};

type ClinicArrivalTasksGridActions = {
    actions: {
        updateSelectedRows: (val?: GridRowSelectionModel) => void;
        setExpandedRows: (ids: GridRowId[]) => void;
        handleExpandAll: (rows: HotelClinicTaskResolved[]) => void;
        updateShowPassengerStatusUpdateDialog: (val?: ShowPassengerStatusUpdateDialogType) => void;
        updateStatusFilter: (status: HotelClinicStatus | null) => void;
        updateTaskStatusHistoryTaskId: (taskId?: string | null) => void;
        updateBulkTasksConfirm: (val?: ClinicArrivalTasksBulkConfirmState | null) => void;
        updateFullScreen: (val?: boolean) => void;
        setSaveConfirmOpen: (open: boolean) => void;
        updateShowTasksDetailUnassignDialog: (val?: ShowTasksDetailUnassignDialogType) => void;
        handleRowModesModelChange: (rowModesModel: GridRowModesModel) => void;
        resetRowChanges: (ids?: GridRowId[]) => void;
        resetRowInitials: (ids?: GridRowId[]) => void;
        getChangedKeys: (
            newRow: HotelClinicTaskResolved,
            oldRow: HotelClinicTaskResolved
        ) => [ClinicTasksEditableFields, number];
        processRowUpdate: (newRow: HotelClinicTaskResolved, oldRow: HotelClinicTaskResolved) => HotelClinicTaskResolved;
        handleRowModesModelCancel: (
            rowId: GridRowId,
            apiRef: GridApiCommon,
            setInitial?: boolean,
            fieldKey?: keyof ClinicTasksEditableFields
        ) => Promise<void>;
    };
};

export const useClinicArrivalTasksGridStore = create<ClinicArrivalTasksGridState & ClinicArrivalTasksGridActions>()(
    (set, get) => ({
        selectedRows: [],
        expandedRows: [],
        showPassengerStatusUpdateDialog: null,
        statusFilter: null,
        taskStatusHistoryTaskId: null,
        bulkTasksConfirm: null,
        isFullScreen: false,
        rowModesModel: {},
        changes: {},
        initialValues: {},
        saveConfirmOpen: false,
        showTasksDetailUnassignDialog: null,
        actions: {
            updateSelectedRows: (val) => set({ selectedRows: val ?? [] }),
            setExpandedRows: (ids) => set({ expandedRows: ids }),
            handleExpandAll: (rows) => {
                const expandedRows = get().expandedRows;
                if (expandedRows.length === rows.length) {
                    set({ expandedRows: [] });
                } else {
                    set({ expandedRows: rows.map((row) => row.id) });
                }
            },
            updateShowPassengerStatusUpdateDialog: (val) => set({ showPassengerStatusUpdateDialog: val ?? null }),
            updateStatusFilter: (status) => set({ statusFilter: status }),
            updateTaskStatusHistoryTaskId: (taskId) => set({ taskStatusHistoryTaskId: taskId ?? null }),
            updateBulkTasksConfirm: (val) => set({ bulkTasksConfirm: val ?? null }),
            updateFullScreen: (isFullScreen) => set({ isFullScreen: isFullScreen ?? !get().isFullScreen }),
            setSaveConfirmOpen: (open) => set({ saveConfirmOpen: open }),
            updateShowTasksDetailUnassignDialog: (val) => set({ showTasksDetailUnassignDialog: val ?? null }),
            handleRowModesModelChange: (rowModesModel) => set({ rowModesModel }),
            resetRowChanges: (ids?: GridRowId[]) => {
                if (!ids) {
                    set({ changes: {} });
                    return;
                }
                const next = { ...get().changes };
                for (const id of ids) delete next[id];
                set({ changes: next });
            },
            resetRowInitials: (ids?: GridRowId[]) => {
                if (!ids) {
                    set({ initialValues: {} });
                    return;
                }
                const next = { ...get().initialValues };
                for (const id of ids) delete next[id];
                set({ initialValues: next });
            },
            getChangedKeys: (newRow: HotelClinicTaskResolved, oldRow: HotelClinicTaskResolved) => {
                const delta: ClinicTasksEditableFields = {};

                const oldP = normalizeTaskPickUp(oldRow.pickUp);
                const newP = normalizeTaskPickUp(newRow.pickUp);
                if (newP && newP !== oldP) {
                    delta.pickUp = newP;
                }
                return [delta, Object.keys(delta).length] as [ClinicTasksEditableFields, number];
            },
            processRowUpdate: (newRow: HotelClinicTaskResolved, oldRow: HotelClinicTaskResolved) => {
                const id = taskRowKey(newRow);
                const [changedFields, changesAmount] = get().actions.getChangedKeys(newRow, oldRow);
                if (!changesAmount) return oldRow;

                const merged = { ...get().changes };

                try {
                    for (const key of Object.keys(changedFields) as (keyof ClinicTasksEditableFields)[]) {
                        const initialData = get().initialValues[id];
                        const initialValue =
                            initialData && get().initialValues[id][key as keyof ClinicTasksEditableFields];
                        const changedVal = changedFields[key];

                        if (changedVal === initialValue) {
                            delete changedFields[key];
                            if (merged[id] && !Object.keys(merged[id]).length) delete merged[id];
                        }

                        if (!initialData || !(key in initialData)) {
                            const nextInitial = { ...get().initialValues };
                            nextInitial[id] = {
                                ...(nextInitial[id] || {}),
                                [key]: normalizeTaskPickUp(oldRow.pickUp),
                            };
                            set({ initialValues: nextInitial });
                        }
                    }
                } catch (err) {
                    console.error(err);
                }

                if (merged[id] || Object.keys(changedFields).length) {
                    merged[id] = { ...(merged[id] || {}), ...changedFields };
                }
                set({ changes: merged });

                return { ...newRow, pickUp: normalizeTaskPickUp(newRow.pickUp) };
            },
            handleRowModesModelCancel: async (rowId, apiRef, setInitial = true, fieldKey) => {
                try {
                    if (!apiRef) return;
                    for (const field of clinicTasksEditableFieldKeysToCancel) {
                        if (fieldKey && fieldKey !== field) continue;
                        const initialData = get().initialValues[rowId];
                        const initialValue = initialData && get().initialValues[rowId][field];
                        const cell = { id: rowId, field };
                        const cellMode = apiRef.getCellMode(rowId, field);

                        if (!setInitial || !initialData || !(field in initialData)) {
                            if (cellMode === GridCellModes.Edit) apiRef.stopCellEditMode(cell);
                            continue;
                        }

                        if (cellMode !== GridCellModes.Edit) {
                            await apiRef.startCellEditMode(cell);
                        }
                        await wait(25);
                        const restore =
                            typeof initialValue === "string"
                                ? DateTime.fromISO(initialValue).isValid
                                    ? DateTime.fromISO(initialValue as string)
                                    : initialValue
                                : initialValue;
                        await apiRef.setEditCellValue({
                            id: rowId,
                            field,
                            value: restore ?? null,
                        });
                        await wait(25);
                        apiRef.stopCellEditMode(cell);
                    }
                } catch (err) {
                    console.error("#clinicTasksGrid handleRowModesModelCancel", err);
                }

                await wait(60);
                get().actions.resetRowChanges([rowId]);
                get().actions.resetRowInitials([rowId]);
                get().actions.handleRowModesModelChange({
                    ...get().rowModesModel,
                    [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
                });
            },
        },
    })
);

export const useClinicArrivalTasksGridSelectedRows = () =>
    useClinicArrivalTasksGridStore((state) => state.selectedRows);
export const useClinicArrivalTasksGridExpandedRows = () =>
    useClinicArrivalTasksGridStore((state) => state.expandedRows);
export const useClinicArrivalTasksGridActions = () => useClinicArrivalTasksGridStore((state) => state.actions);

export const useClinicArrivalPassengerStatusUpdateDialog = () =>
    useClinicArrivalTasksGridStore((state) => state.showPassengerStatusUpdateDialog);

export const useClinicArrivalTasksStatusFilter = () => useClinicArrivalTasksGridStore((state) => state.statusFilter);

export const useClinicArrivalTaskStatusHistoryTaskId = () =>
    useClinicArrivalTasksGridStore((state) => state.taskStatusHistoryTaskId);

export const useClinicArrivalTasksBulkConfirm = () => useClinicArrivalTasksGridStore((state) => state.bulkTasksConfirm);

export const useClinicArrivalTasksIsFullScreen = () => useClinicArrivalTasksGridStore((state) => state.isFullScreen);

export const useClinicArrivalTasksGridChanges = () => useClinicArrivalTasksGridStore((state) => state.changes);

export const useClinicArrivalTasksGridSaveConfirmOpen = () =>
    useClinicArrivalTasksGridStore((state) => state.saveConfirmOpen);

export const useClinicArrivalTasksDetailUnassignDialog = () =>
    useClinicArrivalTasksGridStore((state) => state.showTasksDetailUnassignDialog);

export const useClinicArrivalTasksRowModesModel = () => useClinicArrivalTasksGridStore((state) => state.rowModesModel);

export const useClinicArrivalTasksGridRowChanges = (id: GridRowId) =>
    useClinicArrivalTasksGridStore((state) => state.changes[id]);

export const useClinicArrivalTasksGridIsChanged = (id: GridRowId, field: keyof ClinicTasksEditableFields) =>
    useClinicArrivalTasksGridStore((state) => state.changes[id] && field in state.changes[id]);

export const useClinicArrivalTasksGridInitialVal = (id: GridRowId, field: keyof ClinicTasksEditableFields) =>
    useClinicArrivalTasksGridStore((state) => state.initialValues[id] && state.initialValues[id][field]);
